import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';
import {
    readCachedLocale,
    writeCachedLocale,
    getCachedVersion,
    patchCachedLocaleCell,
    pruneCachedLocaleEntity,
} from '../utils/masterLocaleStore';

// ==================== LANGUAGE CRUD ====================

// Create Language
export const createLanguage = createAsyncThunk(
    'translation/createLanguage',
    async ({ name, code }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/translation/language`, {
                name,
                code
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create language'
            });
        }
    }
);

// Get All Languages
export const getLanguages = createAsyncThunk(
    'translation/getLanguages',
    async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (page) params.append('page', page);
            if (limit) params.append('limit', limit);
            if (search) params.append('search', search);
            
            const url = `${baseURL}/translation/language${params.toString() ? '?' + params.toString() : ''}`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch languages'
            });
        }
    }
);

// Get Language by ID
export const getLanguageById = createAsyncThunk(
    'translation/getLanguageById',
    async (languageId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/language/${languageId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch language'
            });
        }
    }
);

// Update Language
export const updateLanguage = createAsyncThunk(
    'translation/updateLanguage',
    async ({ languageId, name, code }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = {};
            if (name) body.name = name;
            if (code) body.code = code;
            
            const response = await axiosInstance.put(`${baseURL}/translation/language/${languageId}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update language'
            });
        }
    }
);

// Delete Language
// Accepts either a bare languageId (legacy) or { languageId, force } so the
// admin can request a force-delete that reassigns referencing rows to 'en'.
export const deleteLanguage = createAsyncThunk(
    'translation/deleteLanguage',
    async (arg, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        const languageId = typeof arg === 'object' && arg !== null ? arg.languageId : arg;
        const force = typeof arg === 'object' && arg !== null ? !!arg.force : false;
        try {
            const url = `${baseURL}/translation/language/${languageId}${force ? '?force=true' : ''}`;
            const response = await axiosInstance.delete(url);
            return response.data;
        } catch (error) {
            const data = error.response?.data || {};
            return rejectWithValue({
                error: data.message || error.message || 'Failed to delete language',
                status: error.response?.status,
                inUse: !!data.inUse,
                assignedCount: data.assignedCount,
                doctorCount: data.doctorCount,
                patientCount: data.patientCount,
                userCount: data.userCount
            });
        }
    }
);

// ==================== PAGE MODULE CRUD ====================

// Create Page Module
export const createPageModule = createAsyncThunk(
    'translation/createPageModule',
    async ({ pageName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/translation/page-module`, {
                pageName
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create page module'
            });
        }
    }
);

// Get All Page Modules
export const getPageModules = createAsyncThunk(
    'translation/getPageModules',
    async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (page) params.append('page', page);
            if (limit) params.append('limit', limit);
            if (search) params.append('search', search);
            
            const url = `${baseURL}/translation/page-module${params.toString() ? '?' + params.toString() : ''}`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch page modules'
            });
        }
    }
);

// Get Page Module by ID
export const getPageModuleById = createAsyncThunk(
    'translation/getPageModuleById',
    async (uniqueId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/page-module/${uniqueId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch page module'
            });
        }
    }
);

// Update Page Module
export const updatePageModule = createAsyncThunk(
    'translation/updatePageModule',
    async ({ uniqueId, pageName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/translation/page-module/${uniqueId}`, {
                pageName
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update page module'
            });
        }
    }
);

// Delete Page Module
export const deletePageModule = createAsyncThunk(
    'translation/deletePageModule',
    async (uniqueId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/translation/page-module/${uniqueId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to delete page module'
            });
        }
    }
);

// Get Page Module with Keys and Translations
export const getPageModuleWithKeys = createAsyncThunk(
    'translation/getPageModuleWithKeys',
    async ({ uniqueId, page = 1, limit = 10 }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (page) params.append('page', page);
            if (limit) params.append('limit', limit);
            
            const url = `${baseURL}/translation/page-module/${uniqueId}/with-keys${params.toString() ? '?' + params.toString() : ''}`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch page module with keys'
            });
        }
    }
);

// ==================== PAGE MODULE KEYS CRUD ====================

// Create Page Module Key
export const createPageModuleKey = createAsyncThunk(
    'translation/createPageModuleKey',
    async ({ pageId, keyName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/translation/page-module-keys`, {
                pageId,
                keyName
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create page module key'
            });
        }
    }
);

// Get All Page Module Keys
export const getAllPageModuleKeys = createAsyncThunk(
    'translation/getAllPageModuleKeys',
    async ({ page = 1, limit = 10, pageId, keyName, search = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (page) params.append('page', page);
            if (limit) params.append('limit', limit);
            if (pageId) params.append('pageId', pageId);
            if (keyName) params.append('keyName', keyName);
            if (search) params.append('search', search);
            
            const url = `${baseURL}/translation/page-module-keys${params.toString() ? '?' + params.toString() : ''}`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch page module keys'
            });
        }
    }
);

// Get Page Module Key by UniqueId
export const getPageModuleKeyById = createAsyncThunk(
    'translation/getPageModuleKeyById',
    async (uniqueId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/page-module-keys/unique/${uniqueId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch page module key'
            });
        }
    }
);

// Update Page Module Key by UniqueId
export const updatePageModuleKey = createAsyncThunk(
    'translation/updatePageModuleKey',
    async ({ uniqueId, pageId, keyName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = {};
            if (pageId) body.pageId = pageId;
            if (keyName) body.keyName = keyName;
            
            const response = await axiosInstance.put(`${baseURL}/translation/page-module-keys/unique/${uniqueId}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update page module key'
            });
        }
    }
);

// Delete Page Module Key by UniqueId
export const deletePageModuleKey = createAsyncThunk(
    'translation/deletePageModuleKey',
    async (uniqueId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/translation/page-module-keys/unique/${uniqueId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to delete page module key'
            });
        }
    }
);

// ==================== TRANSLATION DATA CRUD ====================

// Create Translation Data
export const createTranslationData = createAsyncThunk(
    'translation/createTranslationData',
    async ({ lvid, lcode, value }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/translation/translation-data`, {
                lvid,
                lcode,
                value
            });
            return response.data;
        } catch (error) {
            const lang = localStorage.getItem('i18nextLng') || 'en';
            const errorMessage = typeof error.response?.data?.message === 'object' 
                ? (error.response.data.message[lang] || error.response.data.message.en || error.response.data.message.fr || 'Failed to create translation data')
                : (error.response?.data?.message || error.message || 'Failed to create translation data');
            return rejectWithValue({
                error: errorMessage
            });
        }
    }
);

// Get All Translation Data
export const getAllTranslationData = createAsyncThunk(
    'translation/getAllTranslationData',
    async ({ page = 1, limit = 10, lvid, lcode, search = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (page) params.append('page', page);
            if (limit) params.append('limit', limit);
            if (lvid) params.append('lvid', lvid);
            if (lcode) params.append('lcode', lcode);
            if (search) params.append('search', search);
            
            const url = `${baseURL}/translation/translation-data${params.toString() ? '?' + params.toString() : ''}`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch translation data'
            });
        }
    }
);

// Get Translation Data by UniqueId
export const getTranslationDataById = createAsyncThunk(
    'translation/getTranslationDataById',
    async (uniqueId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/translation-data/unique/${uniqueId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch translation data'
            });
        }
    }
);

// Get Translation Data by Lvid
export const getTranslationDataByLvid = createAsyncThunk(
    'translation/getTranslationDataByLvid',
    async (lvid, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/translation-data/lvid/${lvid}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch translation data'
            });
        }
    }
);

// Update Translation Data by UniqueId
export const updateTranslationData = createAsyncThunk(
    'translation/updateTranslationData',
    async ({ uniqueId, lvid, lcode, value }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = {};
            if (lvid) body.lvid = lvid;
            if (lcode) body.lcode = lcode;
            if (value) body.value = value;
            
            const response = await axiosInstance.put(`${baseURL}/translation/translation-data/unique/${uniqueId}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update translation data'
            });
        }
    }
);

// Update Translation Data by Lvid and Lcode (Upsert)
export const upsertTranslationData = createAsyncThunk(
    'translation/upsertTranslationData',
    async ({ lvid, lcode, value }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/translation/translation-data/lvid/${lvid}/lcode/${lcode}`, {
                value
            });
            return response.data;
        } catch (error) {
            const lang = localStorage.getItem('i18nextLng') || 'en';
            const errorMessage = typeof error.response?.data?.message === 'object' 
                ? (error.response.data.message[lang] || error.response.data.message.en || error.response.data.message.fr || 'Failed to upsert translation data')
                : (error.response?.data?.message || error.message || 'Failed to upsert translation data');
            return rejectWithValue({
                error: errorMessage
            });
        }
    }
);

// Delete Translation Data by UniqueId
export const deleteTranslationData = createAsyncThunk(
    'translation/deleteTranslationData',
    async (uniqueId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/translation/translation-data/unique/${uniqueId}`);
            return response.data;
        } catch (error) {
            const lang = localStorage.getItem('i18nextLng') || 'en';
            const errorMessage = typeof error.response?.data?.message === 'object' 
                ? (error.response.data.message[lang] || error.response.data.message.en || error.response.data.message.fr || 'Failed to delete translation data')
                : (error.response?.data?.message || error.message || 'Failed to delete translation data');
            return rejectWithValue({
                error: errorMessage
            });
        }
    }
);

// Delete Translation Data by Lvid and Lcode
export const deleteTranslationDataByLvidAndLcode = createAsyncThunk(
    'translation/deleteTranslationDataByLvidAndLcode',
    async ({ lvid, lcode }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/translation/translation-data/lvid/${lvid}/lcode/${lcode}`);
            return response.data;
        } catch (error) {
            const lang = localStorage.getItem('i18nextLng') || 'en';
            const errorMessage = typeof error.response?.data?.message === 'object' 
                ? (error.response.data.message[lang] || error.response.data.message.en || error.response.data.message.fr || 'Failed to delete translation data')
                : (error.response?.data?.message || error.message || 'Failed to delete translation data');
            return rejectWithValue({
                error: errorMessage
            });
        }
    }
);

// ==================== MASTER DATA TRANSLATION ====================

// Get the list of master modules that expose their data for translation.
export const getMasterModules = createAsyncThunk(
    'translation/getMasterModules',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/master/modules`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch master modules'
            });
        }
    }
);

// Get every translation row for a specific master module together with available languages.
export const getMasterTranslations = createAsyncThunk(
    'translation/getMasterTranslations',
    async (moduleName, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/master/${moduleName}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch master translations'
            });
        }
    }
);

// Upsert a single translation cell (keyId, lcode) -> value. Empty string clears it.
// Callers should also pass `moduleName`, `entityId`, `field` so we can patch the
// localStorage cache + Redux in-memory tree without needing to refetch the entire
// locale after a single cell edit.
export const upsertMasterTranslationValue = createAsyncThunk(
    'translation/upsertMasterTranslationValue',
    async ({ keyId, lcode, value, moduleName, entityId, field }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/translation/master/value`, {
                keyId,
                lcode,
                value
            });

            // Keep localStorage in sync immediately so a page reload or a render elsewhere
            // in the app sees the new value without going back to the API.
            if (moduleName && entityId && field) {
                patchCachedLocaleCell(lcode, moduleName, entityId, field, value);
            }

            return { ...response.data, moduleName, entityId, field };
        } catch (error) {
            const lang = localStorage.getItem('i18nextLng') || 'en';
            const raw = error.response?.data?.message;
            const errorMessage = typeof raw === 'object'
                ? (raw[lang] || raw.en || raw.fr || 'Failed to save translation')
                : (raw || error.message || 'Failed to save translation');
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// Fetch the master-data locale tree for a single language and cache it in localStorage.
// The backend returns { lcode, version, data } where `data` is a pre-stringified JSON
// tree — we hand that stringified blob straight to localStorage to avoid a re-serialise.
// Pass { force: true } to bypass the version-based short-circuit and refetch anyway.
export const fetchMasterLocale = createAsyncThunk(
    'translation/fetchMasterLocale',
    async ({ lcode, force = false } = {}, { rejectWithValue }) => {
        if (!lcode) return rejectWithValue({ error: 'lcode is required' });
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/translation/master/export/${lcode}`);
            const payload = response.data || {};
            const dataString = typeof payload.data === 'string' ? payload.data : JSON.stringify(payload.data || {});
            const version = payload.version || 0;

            // Persist the raw string exactly as-received (no re-serialisation cost).
            writeCachedLocale(lcode, dataString, version);

            let tree = {};
            try { tree = JSON.parse(dataString); } catch { tree = {}; }

            return { lcode, version, tree };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch master locale'
            });
        }
    }
);

// Hydrate the Redux cache from localStorage on boot / on language switch. Dispatches
// synchronously (no network). Returns null when there is no cached copy for the language.
export const hydrateMasterLocaleFromCache = createAsyncThunk(
    'translation/hydrateMasterLocaleFromCache',
    async (lcode, { rejectWithValue }) => {
        if (!lcode) return rejectWithValue({ error: 'lcode is required' });
        const tree = readCachedLocale(lcode);
        if (!tree) return { lcode, tree: null, version: 0 };
        return { lcode, tree, version: getCachedVersion(lcode) };
    }
);

// Delete a translation key (and all its language rows). Pass moduleName + entityId so
// we can prune the localStorage cache in lockstep.
export const deleteMasterTranslationKey = createAsyncThunk(
    'translation/deleteMasterTranslationKey',
    async ({ keyId, moduleName, entityId, field }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/translation/master/key/${keyId}`);

            if (moduleName && entityId && field) {
                // Clear just this field across every cached language (we may have multiple
                // language caches sitting in localStorage from previous switches).
                const lang = localStorage.getItem('i18nextLng') || 'en';
                patchCachedLocaleCell(lang, moduleName, entityId, field, '');
            }
            return { ...response.data, keyId, moduleName, entityId, field };
        } catch (error) {
            const lang = localStorage.getItem('i18nextLng') || 'en';
            const raw = error.response?.data?.message;
            const errorMessage = typeof raw === 'object'
                ? (raw[lang] || raw.en || raw.fr || 'Failed to delete key')
                : (raw || error.message || 'Failed to delete key');
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// ==================== SLICE ====================

const translationSlice = createSlice({
    name: 'translation',
    initialState: {
        languages: [],
        languagePagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        selectedLanguage: null,
        pageModules: [],
        pageModulePagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        selectedPageModule: null,
        pageModuleWithKeys: null,
        pageModuleKeys: [],
        pageModuleKeysPagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0,
            hasMore: false
        },
        translationData: [],
        translationDataPagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        languageLoading: false,
        languageCreateLoading: false,
        languageUpdateLoading: false,
        pageModuleLoading: false,
        pageModuleCreateLoading: false,
        pageModuleUpdateLoading: false,
        pageModuleWithKeysLoading: false,
        pageModuleKeysLoading: false,
        pageModuleKeyCreateLoading: false,
        pageModuleKeyUpdateLoading: false,
        translationDataLoading: false,
        translationDataCreateLoading: false,
        translationDataUpdateLoading: false,
        // Master-data translation state
        masterModules: [],
        masterTranslationRows: [],
        masterTranslationLanguages: [],
        masterTranslationSourceLcode: 'en',
        masterTranslationLoading: false,
        masterTranslationSaving: false,
        // Master-data locale cache (single active language at a time to keep memory tight).
        // Shape: { masterSpecialty: { SP00042: { name, description } }, ... }
        masterLocaleLang: null,
        masterLocaleTree: {},
        masterLocaleVersion: 0,
        masterLocaleLoading: false,
        error: null,
        success: false
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = false;
        },
        setSelectedLanguage: (state, action) => {
            state.selectedLanguage = action.payload;
        },
        setSelectedPageModule: (state, action) => {
            state.selectedPageModule = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create Language
            .addCase(createLanguage.pending, (state) => {
                state.languageCreateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createLanguage.fulfilled, (state, action) => {
                state.languageCreateLoading = false;
                state.success = true;
            })
            .addCase(createLanguage.rejected, (state, action) => {
                state.languageCreateLoading = false;
                state.error = action.payload?.error || 'Failed to create language';
            })
            // Get Languages
            .addCase(getLanguages.pending, (state) => {
                state.languageLoading = true;
                state.error = null;
            })
            .addCase(getLanguages.fulfilled, (state, action) => {
                state.languageLoading = false;
                state.languages = action.payload?.data || [];
                state.languagePagination = {
                    total: action.payload?.total || 0,
                    page: action.payload?.page || 1,
                    limit: action.payload?.limit || 10,
                    totalPages: action.payload?.totalPages || 0
                };
            })
            .addCase(getLanguages.rejected, (state, action) => {
                state.languageLoading = false;
                state.error = action.payload?.error || 'Failed to fetch languages';
            })
            // Get Language by ID
            .addCase(getLanguageById.pending, (state) => {
                state.languageLoading = true;
                state.error = null;
            })
            .addCase(getLanguageById.fulfilled, (state, action) => {
                state.languageLoading = false;
                state.selectedLanguage = action.payload?.language || null;
            })
            .addCase(getLanguageById.rejected, (state, action) => {
                state.languageLoading = false;
                state.error = action.payload?.error || 'Failed to fetch language';
            })
            // Update Language
            .addCase(updateLanguage.pending, (state) => {
                state.languageUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateLanguage.fulfilled, (state, action) => {
                state.languageUpdateLoading = false;
                state.success = true;
            })
            .addCase(updateLanguage.rejected, (state, action) => {
                state.languageUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update language';
            })
            // Delete Language
            .addCase(deleteLanguage.pending, (state) => {
                state.languageLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteLanguage.fulfilled, (state, action) => {
                state.languageLoading = false;
                state.success = true;
            })
            .addCase(deleteLanguage.rejected, (state, action) => {
                state.languageLoading = false;
                state.error = action.payload?.error || 'Failed to delete language';
            })
            // Create Page Module
            .addCase(createPageModule.pending, (state) => {
                state.pageModuleCreateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createPageModule.fulfilled, (state, action) => {
                state.pageModuleCreateLoading = false;
                state.success = true;
            })
            .addCase(createPageModule.rejected, (state, action) => {
                state.pageModuleCreateLoading = false;
                state.error = action.payload?.error || 'Failed to create page module';
            })
            // Get Page Modules
            .addCase(getPageModules.pending, (state) => {
                state.pageModuleLoading = true;
                state.error = null;
            })
            .addCase(getPageModules.fulfilled, (state, action) => {
                state.pageModuleLoading = false;
                state.pageModules = action.payload?.data || [];
                state.pageModulePagination = {
                    total: action.payload?.total || 0,
                    page: action.payload?.page || 1,
                    limit: action.payload?.limit || 10,
                    totalPages: action.payload?.totalPages || 0
                };
            })
            .addCase(getPageModules.rejected, (state, action) => {
                state.pageModuleLoading = false;
                state.error = action.payload?.error || 'Failed to fetch page modules';
            })
            // Get Page Module by ID
            .addCase(getPageModuleById.pending, (state) => {
                state.pageModuleLoading = true;
                state.error = null;
            })
            .addCase(getPageModuleById.fulfilled, (state, action) => {
                state.pageModuleLoading = false;
                state.selectedPageModule = action.payload?.pageModule || null;
            })
            .addCase(getPageModuleById.rejected, (state, action) => {
                state.pageModuleLoading = false;
                state.error = action.payload?.error || 'Failed to fetch page module';
            })
            // Update Page Module
            .addCase(updatePageModule.pending, (state) => {
                state.pageModuleUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updatePageModule.fulfilled, (state, action) => {
                state.pageModuleUpdateLoading = false;
                state.success = true;
            })
            .addCase(updatePageModule.rejected, (state, action) => {
                state.pageModuleUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update page module';
            })
            // Delete Page Module
            .addCase(deletePageModule.pending, (state) => {
                state.pageModuleLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deletePageModule.fulfilled, (state, action) => {
                state.pageModuleLoading = false;
                state.success = true;
            })
            .addCase(deletePageModule.rejected, (state, action) => {
                state.pageModuleLoading = false;
                state.error = action.payload?.error || 'Failed to delete page module';
            })
            // Get Page Module with Keys
            .addCase(getPageModuleWithKeys.pending, (state) => {
                state.pageModuleWithKeysLoading = true;
                state.error = null;
            })
            .addCase(getPageModuleWithKeys.fulfilled, (state, action) => {
                state.pageModuleWithKeysLoading = false;
                state.pageModuleWithKeys = action.payload?.data || null;
                if (action.payload?.pagination) {
                    state.pageModuleKeysPagination = {
                        total: action.payload.pagination.total || 0,
                        page: action.payload.pagination.page || 1,
                        limit: action.payload.pagination.limit || 10,
                        totalPages: action.payload.pagination.totalPages || 0,
                        hasMore: action.payload.pagination.hasMore || false
                    };
                }
            })
            .addCase(getPageModuleWithKeys.rejected, (state, action) => {
                state.pageModuleWithKeysLoading = false;
                state.error = action.payload?.error || 'Failed to fetch page module with keys';
            })
            // Create Page Module Key
            .addCase(createPageModuleKey.pending, (state) => {
                state.pageModuleKeyCreateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createPageModuleKey.fulfilled, (state, action) => {
                state.pageModuleKeyCreateLoading = false;
                state.success = true;
            })
            .addCase(createPageModuleKey.rejected, (state, action) => {
                state.pageModuleKeyCreateLoading = false;
                state.error = action.payload?.error || 'Failed to create page module key';
            })
            // Get All Page Module Keys
            .addCase(getAllPageModuleKeys.pending, (state) => {
                state.pageModuleKeysLoading = true;
                state.error = null;
            })
            .addCase(getAllPageModuleKeys.fulfilled, (state, action) => {
                state.pageModuleKeysLoading = false;
                state.pageModuleKeys = action.payload?.data || [];
                state.pageModuleKeysPagination = {
                    total: action.payload?.total || 0,
                    page: action.payload?.page || 1,
                    limit: action.payload?.limit || 10,
                    totalPages: action.payload?.totalPages || 0
                };
            })
            .addCase(getAllPageModuleKeys.rejected, (state, action) => {
                state.pageModuleKeysLoading = false;
                state.error = action.payload?.error || 'Failed to fetch page module keys';
            })
            // Get Page Module Key by ID
            .addCase(getPageModuleKeyById.pending, (state) => {
                state.pageModuleKeysLoading = true;
                state.error = null;
            })
            .addCase(getPageModuleKeyById.fulfilled, (state, action) => {
                state.pageModuleKeysLoading = false;
            })
            .addCase(getPageModuleKeyById.rejected, (state, action) => {
                state.pageModuleKeysLoading = false;
                state.error = action.payload?.error || 'Failed to fetch page module key';
            })
            // Update Page Module Key
            .addCase(updatePageModuleKey.pending, (state) => {
                state.pageModuleKeyUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updatePageModuleKey.fulfilled, (state, action) => {
                state.pageModuleKeyUpdateLoading = false;
                state.success = true;
            })
            .addCase(updatePageModuleKey.rejected, (state, action) => {
                state.pageModuleKeyUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update page module key';
            })
            // Delete Page Module Key
            .addCase(deletePageModuleKey.pending, (state) => {
                state.pageModuleKeysLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deletePageModuleKey.fulfilled, (state, action) => {
                state.pageModuleKeysLoading = false;
                state.success = true;
            })
            .addCase(deletePageModuleKey.rejected, (state, action) => {
                state.pageModuleKeysLoading = false;
                state.error = action.payload?.error || 'Failed to delete page module key';
            })
            // Create Translation Data
            .addCase(createTranslationData.pending, (state) => {
                state.translationDataCreateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createTranslationData.fulfilled, (state, action) => {
                state.translationDataCreateLoading = false;
                state.success = true;
            })
            .addCase(createTranslationData.rejected, (state, action) => {
                state.translationDataCreateLoading = false;
                state.error = action.payload?.error || 'Failed to create translation data';
            })
            // Get All Translation Data
            .addCase(getAllTranslationData.pending, (state) => {
                state.translationDataLoading = true;
                state.error = null;
            })
            .addCase(getAllTranslationData.fulfilled, (state, action) => {
                state.translationDataLoading = false;
                state.translationData = action.payload?.data || [];
                state.translationDataPagination = {
                    total: action.payload?.total || 0,
                    page: action.payload?.page || 1,
                    limit: action.payload?.limit || 10,
                    totalPages: action.payload?.totalPages || 0
                };
            })
            .addCase(getAllTranslationData.rejected, (state, action) => {
                state.translationDataLoading = false;
                state.error = action.payload?.error || 'Failed to fetch translation data';
            })
            // Get Translation Data by ID
            .addCase(getTranslationDataById.pending, (state) => {
                state.translationDataLoading = true;
                state.error = null;
            })
            .addCase(getTranslationDataById.fulfilled, (state, action) => {
                state.translationDataLoading = false;
            })
            .addCase(getTranslationDataById.rejected, (state, action) => {
                state.translationDataLoading = false;
                state.error = action.payload?.error || 'Failed to fetch translation data';
            })
            // Get Translation Data by Lvid
            .addCase(getTranslationDataByLvid.pending, (state) => {
                state.translationDataLoading = true;
                state.error = null;
            })
            .addCase(getTranslationDataByLvid.fulfilled, (state, action) => {
                state.translationDataLoading = false;
                state.translationData = action.payload?.data || [];
            })
            .addCase(getTranslationDataByLvid.rejected, (state, action) => {
                state.translationDataLoading = false;
                state.error = action.payload?.error || 'Failed to fetch translation data';
            })
            // Update Translation Data
            .addCase(updateTranslationData.pending, (state) => {
                state.translationDataUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateTranslationData.fulfilled, (state, action) => {
                state.translationDataUpdateLoading = false;
                state.success = true;
            })
            .addCase(updateTranslationData.rejected, (state, action) => {
                state.translationDataUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update translation data';
            })
            // Upsert Translation Data
            .addCase(upsertTranslationData.pending, (state) => {
                state.translationDataUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(upsertTranslationData.fulfilled, (state, action) => {
                state.translationDataUpdateLoading = false;
                state.success = true;
            })
            .addCase(upsertTranslationData.rejected, (state, action) => {
                state.translationDataUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to upsert translation data';
            })
            // Delete Translation Data
            .addCase(deleteTranslationData.pending, (state) => {
                state.translationDataLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteTranslationData.fulfilled, (state, action) => {
                state.translationDataLoading = false;
                state.success = true;
            })
            .addCase(deleteTranslationData.rejected, (state, action) => {
                state.translationDataLoading = false;
                state.error = action.payload?.error || 'Failed to delete translation data';
            })
            // Delete Translation Data by Lvid and Lcode
            .addCase(deleteTranslationDataByLvidAndLcode.pending, (state) => {
                state.translationDataLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteTranslationDataByLvidAndLcode.fulfilled, (state, action) => {
                state.translationDataLoading = false;
                state.success = true;
            })
            .addCase(deleteTranslationDataByLvidAndLcode.rejected, (state, action) => {
                state.translationDataLoading = false;
                state.error = action.payload?.error || 'Failed to delete translation data';
            })
            // Master modules
            .addCase(getMasterModules.fulfilled, (state, action) => {
                state.masterModules = action.payload?.modules || [];
            })
            .addCase(getMasterModules.rejected, (state, action) => {
                state.error = action.payload?.error || 'Failed to fetch master modules';
            })
            // Master translations list
            .addCase(getMasterTranslations.pending, (state) => {
                state.masterTranslationLoading = true;
                state.error = null;
            })
            .addCase(getMasterTranslations.fulfilled, (state, action) => {
                state.masterTranslationLoading = false;
                state.masterTranslationRows = action.payload?.rows || [];
                state.masterTranslationLanguages = action.payload?.languages || [];
                state.masterTranslationSourceLcode = action.payload?.sourceLcode || 'en';
            })
            .addCase(getMasterTranslations.rejected, (state, action) => {
                state.masterTranslationLoading = false;
                state.error = action.payload?.error || 'Failed to fetch master translations';
            })
            // Upsert master translation cell
            .addCase(upsertMasterTranslationValue.pending, (state) => {
                state.masterTranslationSaving = true;
                state.error = null;
                state.success = false;
            })
            .addCase(upsertMasterTranslationValue.fulfilled, (state, action) => {
                state.masterTranslationSaving = false;
                state.success = true;
                const { keyId, lcode, value, moduleName, entityId, field } = action.payload || {};
                if (!keyId || !lcode) return;

                // Admin table row state.
                const row = state.masterTranslationRows.find((r) => r.keyId === keyId);
                if (row) {
                    if (!row.translations) row.translations = {};
                    if (value === '') {
                        delete row.translations[lcode];
                    } else {
                        row.translations[lcode] = value;
                    }
                }

                // Live in-memory tree consumed by useMasterLabel. Only patch when the
                // cell's language matches the active cached locale.
                if (moduleName && entityId && field && state.masterLocaleLang === lcode) {
                    if (!state.masterLocaleTree[moduleName]) state.masterLocaleTree[moduleName] = {};
                    if (!state.masterLocaleTree[moduleName][entityId]) {
                        state.masterLocaleTree[moduleName][entityId] = {};
                    }
                    if (value === '') {
                        delete state.masterLocaleTree[moduleName][entityId][field];
                    } else {
                        state.masterLocaleTree[moduleName][entityId][field] = value;
                    }
                }
            })
            .addCase(upsertMasterTranslationValue.rejected, (state, action) => {
                state.masterTranslationSaving = false;
                state.error = action.payload?.error || 'Failed to save translation';
            })
            // Delete master translation key
            .addCase(deleteMasterTranslationKey.pending, (state) => {
                state.masterTranslationSaving = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteMasterTranslationKey.fulfilled, (state, action) => {
                state.masterTranslationSaving = false;
                state.success = true;
                const { keyId, moduleName, entityId, field } = action.payload || {};
                if (keyId) {
                    state.masterTranslationRows = state.masterTranslationRows.filter((r) => r.keyId !== keyId);
                }
                // Prune the in-memory locale tree in step with the admin-table state.
                if (moduleName && entityId && field && state.masterLocaleTree?.[moduleName]?.[entityId]) {
                    delete state.masterLocaleTree[moduleName][entityId][field];
                    if (Object.keys(state.masterLocaleTree[moduleName][entityId]).length === 0) {
                        delete state.masterLocaleTree[moduleName][entityId];
                    }
                    if (Object.keys(state.masterLocaleTree[moduleName] || {}).length === 0) {
                        delete state.masterLocaleTree[moduleName];
                    }
                }
            })
            .addCase(deleteMasterTranslationKey.rejected, (state, action) => {
                state.masterTranslationSaving = false;
                state.error = action.payload?.error || 'Failed to delete key';
            })
            // Fetch master locale (network)
            .addCase(fetchMasterLocale.pending, (state, action) => {
                state.masterLocaleLoading = true;
                state.error = null;
                const lcode = action.meta?.arg?.lcode;
                if (lcode) state.masterLocaleLang = lcode;
            })
            .addCase(fetchMasterLocale.fulfilled, (state, action) => {
                state.masterLocaleLoading = false;
                const { lcode, version, tree } = action.payload || {};
                if (lcode) {
                    state.masterLocaleLang = lcode;
                    state.masterLocaleTree = tree || {};
                    state.masterLocaleVersion = version || 0;
                }
            })
            .addCase(fetchMasterLocale.rejected, (state, action) => {
                state.masterLocaleLoading = false;
                state.error = action.payload?.error || 'Failed to fetch master locale';
            })
            // Hydrate master locale from localStorage (synchronous, no network)
            .addCase(hydrateMasterLocaleFromCache.fulfilled, (state, action) => {
                const { lcode, tree, version } = action.payload || {};
                if (!lcode) return;
                state.masterLocaleLang = lcode;
                if (tree) {
                    state.masterLocaleTree = tree;
                    state.masterLocaleVersion = version || 0;
                }
            });
    }
});

export const { clearError, clearSuccess, setSelectedLanguage, setSelectedPageModule } = translationSlice.actions;
export default translationSlice.reducer;

